import { FilterRow } from "@components/FilterBar";
import { type DeviceObject, useObjects, useUpdateObject } from "@features/devices/api";
import { PageLayoutWithHelp } from "@features/page-help";
import { extractErrorMessage } from "@shared/extractErrorMessage";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { useStandardPagination } from "@shared/useStandardPagination";
import { App, Button, Form, Input, InputNumber, Popconfirm, Space, Typography } from "antd";
import type { ColumnsType, ColumnType } from "antd/es/table";
import type React from "react";
import { useMemo, useState } from "react";

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: string;
  inputType: "number" | "text";
  record: DeviceObject;
  index?: number;
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  editing,
  dataIndex,
  title,
  inputType,
  children,
  ...restProps
}) => {
  const inputNode = inputType === "number" ? <InputNumber style={{ width: "100%" }} /> : <Input />;

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: false,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const ObjectsPage = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [search, setSearch] = useState("");
  const [editingKey, setEditingKey] = useState("");
  const objectsQuery = useObjects();
  const updateMutation = useUpdateObject();

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return objectsQuery.data ?? [];
    }
    return (objectsQuery.data ?? []).filter(
      (obj) =>
        obj.txt.toLowerCase().includes(term) ||
        (obj.ur_adr ?? "").toLowerCase().includes(term) ||
        obj.n_ob.toString().includes(term),
    );
  }, [objectsQuery.data, search]);

  const isEditing = (record: DeviceObject) => `${record.syb_rnk}/${record.n_ob}` === editingKey;

  const edit = (record: DeviceObject) => {
    form.setFieldsValue({ ur_adr: "", n_dog: null, ...record });
    setEditingKey(`${record.syb_rnk}/${record.n_ob}`);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const save = async (record: DeviceObject) => {
    try {
      const row = await form.validateFields();

      await updateMutation.mutateAsync({
        regionId: record.syb_rnk,
        objectId: record.n_ob,
        data: {
          ur_adr: row.ur_adr || null,
          n_dog: row.n_dog || null,
        },
      });

      setEditingKey("");
      message.success("Объект обновлен");
    } catch (err) {
      message.error(extractErrorMessage(err));
    }
  };

  type EditableColumn = ColumnType<DeviceObject> & { editable?: boolean };

  const columns: EditableColumn[] = [
    { title: "ID", dataIndex: "n_ob", width: 120 },
    { title: "Название", dataIndex: "txt", ellipsis: true },
    {
      title: "Адрес",
      dataIndex: "ur_adr",
      ellipsis: true,
      editable: true,
    },
    {
      title: "Договор",
      dataIndex: "n_dog",
      width: 160,
      editable: true,
    },
    {
      title: "Действия",
      dataIndex: "operation",
      width: 150,
      render: (_: unknown, record: DeviceObject) => {
        const editable = isEditing(record);
        return editable ? (
          <Space>
            <Typography.Link onClick={() => save(record)} style={{ marginInlineEnd: 8 }}>
              Сохранить
            </Typography.Link>
            <Popconfirm title="Отменить изменения?" onConfirm={cancel}>
              <Button type="link">Отмена</Button>
            </Popconfirm>
          </Space>
        ) : (
          <Typography.Link disabled={editingKey !== ""} onClick={() => edit(record)}>
            Изменить
          </Typography.Link>
        );
      },
    },
  ];

  const mergedColumns: ColumnsType<DeviceObject> = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    const dataIndex = String(col.dataIndex ?? "");
    const title = typeof col.title === "string" ? col.title : "";
    return {
      ...col,
      onCell: (record: DeviceObject, index?: number) =>
        ({
          record,
          inputType: dataIndex === "n_dog" ? "number" : "text",
          dataIndex,
          title,
          editing: isEditing(record),
          index,
        }) as unknown as React.HTMLAttributes<HTMLElement>,
    };
  });

  const pagination = useStandardPagination(filtered.length);

  return (
    <PageLayoutWithHelp title="Объекты">
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <FilterRow>
          <Input
            placeholder="Поиск по названию, адресу или номеру"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ maxWidth: 400, minWidth: 280 }}
          />
        </FilterRow>
        <Form form={form} component={false}>
          <EnterpriseTable<DeviceObject>
            components={{
              body: {
                cell: EditableCell,
              },
            }}
            rowKey={(record) => `${record.syb_rnk}/${record.n_ob}`}
            dataSource={filtered}
            loading={objectsQuery.isLoading || updateMutation.isPending}
            columns={mergedColumns}
            scroll={{ x: 1000 }}
            pagination={pagination}
            size="small"
            toolbar={{ reload: true, onReload: () => objectsQuery.refetch() }}
          />
        </Form>
      </Space>
    </PageLayoutWithHelp>
  );
};

export default ObjectsPage;
